import { useState, type FormEvent } from 'react';
import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useCreateApp } from '@/api/hooks';
import { useProvisionEas } from '@/api/hooksEas';
import { firstInvalidField } from '@/lib/formModel';
import { useFeature } from '@/lib/permissions';
import { wordingFor } from '@/lib/audience';
import { useSeesProviders } from '@/lib/useAudience';
import { focusElement, useCreateFormState } from '@/lib/useFormState';
import { FRAMEWORK_LABEL } from '@/components/badges';
import { connectedWithoutEasTitle, offersEasCreate } from './easSetupModel';
import { applyConnectChange, buildConnectBody, connectErrors, initialConnectValues, isNativeOnBitrise, validateConnectApp, type ConnectField } from './appFormModel';

interface Options {
  projectId: string;
  onDone: (app: App) => void;
}

type CreateFormState = ReturnType<typeof useCreateFormState>;

/** The values of the Connect app form, what they imply (EAS or Bitrise), and the messages under each field. */
function useConnectFields(form: CreateFormState) {
  const nativeBuilds = useFeature('bitrise') === true;
  const sourceControl = useFeature('sourceControl') === true;
  const [values, setValues] = useState(() => initialConnectValues(nativeBuilds));
  const [linkExisting, setLinkExisting] = useState(false);
  /** Advanced: the person links an EAS project that already exists instead of letting Hangar create it. */
  const [linkEasExisting, setLinkEasExisting] = useState(false);
  const onBitrise = isNativeOnBitrise(nativeBuilds, values.framework);
  /** Source control is served: Hangar creates the Bitrise app right after the app is connected (unless one is linked by hand). */
  const createOnBitrise = onBitrise && sourceControl && !linkExisting;
  const local = validateConnectApp(values, onBitrise);
  return {
    values,
    local,
    errors: connectErrors(local, form.server.fields, form.submitted, values),
    nativeBuilds,
    sourceControl,
    onBitrise,
    createOnBitrise,
    linkExisting,
    setLinkExisting,
    linkEasExisting,
    setLinkEasExisting: (link: boolean) => {
      setLinkEasExisting(link);
      if (!link) setValues((v) => ({ ...v, easProjectId: '' }));
    },
    bitriseError: form.server.fields.bitriseAppSlug || undefined,
    change: (field: ConnectField, raw: string) => {
      setValues((v) => applyConnectChange(v, field, raw));
      form.clearField(field);
    },
    setBitriseAppSlug: (slug: string) => setValues((v) => ({ ...v, bitriseAppSlug: slug })),
  };
}

type ConnectFields = ReturnType<typeof useConnectFields>;

interface SubmitDeps extends Options {
  create: ReturnType<typeof useCreateApp>;
  provision: ReturnType<typeof useProvisionEas>;
  form: CreateFormState;
  fields: ConnectFields;
  setCreated: (app: App) => void;
  /** A platform operator (SPEC decision 23): toasts name the provider only for them. */
  sees: boolean;
}

/**
 * An Expo / React Native app connected without an EAS project id gets its project from Hangar right away (SPEC §9.1.1).
 * The app is already connected, so a failure is a warning with the API's own wording, and the app page has the retry.
 */
async function createEasProject(app: App, provision: ReturnType<typeof useProvisionEas>, sees: boolean): Promise<void> {
  if (!offersEasCreate(app)) return;
  const say = wordingFor(sees);
  try {
    const setup = await provision.mutateAsync({ appId: app.id });
    if (sees) toast.success('EAS project ready', { description: setup.fullName ?? setup.projectId ?? undefined });
    else toast.success('Build setup ready', { description: `${app.name} can build with Hangar.` });
  } catch (err) {
    const reason = err instanceof ApiError ? say(err.message) : 'Hangar could not reach the build service.';
    toast.warning(connectedWithoutEasTitle(app.name, sees), { description: `${reason} Open the app to try again.` });
  }
}

/** Validates, creates the app and either hands it over or goes on to "Create on Bitrise" in the same sheet. */
function useConnectSubmit({ projectId, onDone, create, provision, form, fields, setCreated, sees }: SubmitDeps) {
  return async (e: FormEvent) => {
    e.preventDefault();
    form.begin();
    const invalid = firstInvalidField(fields.local);
    if (invalid) return focusElement(`app-${invalid}`);
    try {
      const body = buildConnectBody(projectId, fields.values, fields.onBitrise, fields.createOnBitrise);
      const app = await create.mutateAsync({ body, idempotencyKey: form.idempotencyKey() });
      form.renewKey();
      toast.success(`${app.name} connected`, { description: `${app.slug} · ${FRAMEWORK_LABEL[app.framework]}` });
      await createEasProject(app, provision, sees);
      if (fields.createOnBitrise) setCreated(app);
      else onDone(app);
    } catch (err) {
      const first = Object.keys(form.fail(err))[0];
      if (first) focusElement(`app-${first}`);
    }
  };
}

/** Everything the Connect app form needs: its values, derived flags, messages and the submit handler. */
export function useConnectAppForm(options: Options) {
  const create = useCreateApp();
  const provision = useProvisionEas();
  const form = useCreateFormState();
  /** The app once connected, while "Create on Bitrise" runs in the same sheet. */
  const [created, setCreated] = useState<App | null>(null);
  const fields = useConnectFields(form);
  const sees = useSeesProviders();
  const onSubmit = useConnectSubmit({ ...options, create, provision, form, fields, setCreated, sees });
  return { ...fields, created, pending: create.isPending || provision.isPending, formError: form.server.form, onSubmit };
}
